// traceLanes.js — the LANE reading of one trace: the semantic feed's items
// re-bucketed onto a horizontal timeline. The vertical feed (TraceWorkFeed)
// orders operations by time and states each one's subject; this model inverts
// that — every SUBJECT the extractors recovered (a file path, a research host,
// a PR, a query target, a skill, an unclaimed tool) becomes a lane, and the
// turns that touched it become marks along a shared turn axis.
//
// The work half is derived from buildSemanticFeed's deterministic output — the
// same claims, the same residual honesty (unclaimed tool calls get lanes too,
// under an "Unclassified" group), just a different projection. Two things are
// read from the raw turns instead, because they are not tool-call claims and so
// have no place in the feed: sub-agent spawn/merge marks, and the agent's own
// narration (see extractNarration). Neither touches the coverage numbers.
// Pure and DOM-free so the geometry-free half of the view is testable.

import { buildSemanticFeed, feedDomains, promptWordsOf, replyWordsOf } from "./traceSemanticFeed.js";
import { mediaSummary, reconstructFile } from "./traceFileTree.js";
import {
  agentTargetOf,
  codexAgentMessagesIn,
  isAsyncLaunchAck,
  spawnAckTaskName,
  spawnFromToolUse,
  taskNameLabel,
  taskNotificationsIn,
} from "./traceAgentSpawns.js";
import { pathRelations } from "./tracePathAliases.js";
import { callDescriptionOf } from "./shellToolCall.js";
import { actionIdOf, buildTraceActions } from "./traceActions.js";
import { toolResultText } from "./traceToolCall.js";

// Op kinds that AUTHOR something, vs consult it. The mark vocabulary is
// deliberately small — read / write / error for work on a subject, plus
// prompt / reply / note for the conversation's own lanes (rendered as
// diamonds, never as work marks): a lane says what was touched and when;
// what exactly happened is the cell detail's job.
const WRITE_OP_KINDS = new Set(["edit", "write", "push", "publish", "create"]);

const LANE_KIND_FORMS = {
  acted: ["action", "actions"],
  docs: ["doc", "docs"],
  logs: ["log read", "log reads"],
  watched: ["check", "checks"],
};

function laneKindLabel(kind, count) {
  const forms = LANE_KIND_FORMS[kind];
  if (forms) return forms[count === 1 ? 0 : 1];
  if (count === 1) return kind;
  if (/[^aeiou]y$/i.test(kind)) return `${kind.slice(0, -1)}ies`;
  if (/(?:s|x|z|ch|sh)$/i.test(kind)) return `${kind}es`;
  return `${kind}s`;
}

export function markKindOf(item) {
  if (item?.domain === "prompt") return "prompt";
  if (item?.domain === "reply") return "reply";
  if (item?.domain === "narration") return "note";
  // What the harness sent as system is said in passing, like a note; output
  // whose call the record lacks is a call's, read or failed.
  if (item?.domain === "harness") return "note";
  if (item?.domain === "output") return item.op?.ok === false ? "error" : "read";
  const op = item?.op || {};
  if (op.ok === false) return "error";
  // A verified op is a model's on-the-fly reading, not an extractor claim —
  // its mark says so (rendered dashed), whatever work kind it carries.
  if (op.verified) return "verified";
  // A REAL residual call the reading harvested: solid (the call happened,
  // quoted tier) but in the reading tone — the cell itself says it was read,
  // not only its tooltip.
  if (op.readByModel) return "readCall";
  // Sub-agent lifecycle marks read as conversation, not work: the spawn IS a
  // prompt (to the sub-agent) and the merge IS its reply, so they take the
  // ruler-beat diamonds rather than read/write squares. An async launch
  // acknowledgement is neither - metadata, rendered as a consulted mark.
  if (op.kind === "spawn") return "prompt";
  if (op.kind === "merge") return "reply";
  if (op.kind === "ack") return "read";
  // A message INTO the child's thread is a prompt to it, exactly like the
  // spawn; an interrupt acts ON the thread.
  if (op.kind === "send") return "prompt";
  if (op.kind === "interrupt") return "write";
  if (op.create) return "write";
  if (WRITE_OP_KINDS.has(op.kind)) return "write";
  if (item?.domain === "artifact") return "write";
  return "read";
}

const clip = (s, n) => {
  const t = String(s || "");
  return t.length > n ? `${t.slice(0, n - 1)}…` : t;
};

const hostOf = (url) => {
  try {
    return new URL(String(url)).host || null;
  } catch {
    return null;
  }
};

// What a lane IS, per domain: the entity key items group on, and how it reads
// in the rail. Files are handled upstream (they get a real path tree); this
// covers every other domain, including runtime-lens domains it has never seen
// (the generic fallback), so a new lens's ops always land in a lane instead of
// vanishing from the projection.
export function laneEntityOf(domain, op) {
  const o = op || {};
  switch (domain) {
    case "research":
      return o.kind === "search"
        ? { key: "search", label: "web searches" }
        : { key: `host:${hostOf(o.url) || "page"}`, label: hostOf(o.url) || "fetched pages" };
    case "forge": {
      const target = o.num ? `${o.res || "issue"} #${o.num}` : o.repo || o.res || "forge";
      return { key: `t:${target}`, label: target };
    }
    case "linear":
      return { key: `i:${o.issue || "issues"}`, label: o.issue || "issues" };
    case "grafana":
      return { key: `g:${o.instance || o.engine || "grafana"}`, label: o.instance || o.engine || "grafana" };
    case "query":
      return { key: `q:${o.target || o.engine || "query"}`, label: o.target || o.engine || "query" };
    // One lane per repository, not per ref: a lane per revision would mint one
    // for every sha an agent inspected, and the useful reading is the rhythm of
    // version-control work in a working tree over the session. The op carries
    // the verb; the marks carry read against write.
    case "vcs":
      return { key: `r:${o.repo || "working tree"}`, label: o.repo || "working tree" };
    // One lane per tool. The subject a reader follows is "what cargo did across
    // this session", and its failures are the thing worth seeing.
    case "build":
      return { key: `b:${o.tool || "build"}`, label: o.tool || "build" };
    case "recall":
      return o.kind === "docs" ? { key: "docs", label: "docs" } : { key: "search", label: "prior work" };
    case "skill":
      return { key: `s:${o.name || "skill"}`, label: `/${o.name || "skill"}` };
    case "artifact":
      return { key: `a:${o.path || o.title || "artifact"}`, label: o.title || o.path || "artifact" };
    case "code":
      // Code-mode leftovers split into two subjects: the cells themselves
      // (programs nothing was extracted from) and the poll/stdin plumbing.
      return o.kind === "cell"
        ? { key: "cells", label: "code cells" }
        : { key: "polls", label: "cell polls" };
    case "other":
      return { key: `n:${o.name || "tool"}`, label: o.name || "tool" };
    default: {
      const entity = o.path || o.url || o.target || o.issue || o.res || o.name || null;
      return { key: `x:${entity || domain}`, label: entity || null };
    }
  }
}

// One line on what a mark's op did — the cell tooltip and the detail rows.
// A verified op is a model's quote-anchored reading, not an extractor claim;
// the suffix keeps that distinction on every line it appears in.
// How far the command behind an op could reach, when that is worth saying.
// `cwd` is the unremarkable case and stays silent: annotating every ordinary
// read with "cwd" would be noise on the majority of lines and would train a
// reader to stop reading the suffix at all.
const REACH_NOTE = {
  network: "reaches the network",
  system: "reaches outside the project",
  home: "reaches outside the project",
  temp: "writes to temp",
};

// A cell can run several inner commands (often in parallel); the label names
// the first and counts the rest so the others are not silently dropped.
function cellCommandLabel(op) {
  const more = (op.commandCount || 1) - 1;
  return `${clip(op.command.split("\n")[0], 80)}${more > 0 ? ` +${more} more` : ""}`;
}

export function opLine(item) {
  const line = opLineOf(item);
  const op = item?.op || {};
  const suffix = op.verified ? " · verified (model)" : "";
  const reach = REACH_NOTE[op.reach] || null;
  return `${line}${suffix}${reach ? ` · ${reach}` : ""}`;
}

function opLineOf(item) {
  const op = item?.op || {};
  switch (item?.domain) {
    case "prompt":
      return `${op.kind === "notice" ? "notice · " : ""}${clip(op.text, 90)}`;
    case "reply":
      return clip(op.text, 90);
    case "narration":
      return `${op.kind === "thinking" ? "thinking · " : ""}${clip(op.text, 90)}`;
    case "harness":
      return clip(op.text, 90) || "system message";
    case "output":
      return `${op.ok === false ? "error \u00b7 " : ""}${clip(op.text, 90) || "empty result"}`;
    case "files": {
      // A shell-written file has a command instead of a body, and an attachment
      // read has a descriptor instead of lines. Both would otherwise print as a
      // bare op kind, which is the least a reader can be told about either.
      if (op.shell) {
        // The mark says failed for every write in a failed command, because we
        // cannot know where the chain broke. The line is where the reader learns
        // whether the failure was this write's or someone else's further down.
        const exit = op.ok === false
          ? op.shell.exitUnattributed
            ? " · errored · may have landed before the failure"
            : " · errored"
          : "";
        return `wrote via shell · ${clip(op.shell.statement, 80)}${exit}`;
      }
      if (op.media) return `viewed · ${mediaSummary(op.media)}${op.ok === false ? " · errored" : ""}`;
      const delta = (op.plus || 0) + (op.minus || 0) > 0 ? ` · +${op.plus || 0} −${op.minus || 0}` : "";
      const size = !delta && op.readLines ? ` · ${op.readLines} line${op.readLines === 1 ? "" : "s"}` : "";
      // Epistemics on the line: a derived op's content was recovered from the
      // command's own bytes (or, for a sed replay, from believed state) — the
      // reader deserves to know it is reading a derivation, not a quote.
      const basis = op.derived ? ` · derived (${op.basis || "replay"})` : "";
      return `${op.kind || "op"}${delta}${size}${basis}${op.ok === false ? " · errored" : ""}`;
    }
    case "research":
      return op.kind === "search"
        ? `searched “${clip(op.query, 70)}” · ${op.resultCount ?? 0} results`
        : `fetched ${clip(op.url, 80)}`;
    case "forge": {
      const verb = op.create ? "created" : op.kind === "write" ? "wrote" : op.kind === "push" ? "pushed" : op.kind === "browse" ? "browsed" : "read";
      return [verb, op.title ? clip(op.title, 70) : null].filter(Boolean).join(" · ");
    }
    case "linear":
      return [op.create ? "created" : op.kind === "write" ? "wrote" : "read", op.title ? clip(op.title, 70) : null]
        .filter(Boolean)
        .join(" · ");
    case "grafana":
    case "query":
      return `${op.engine || "query"} · ${clip(op.query || op.target || "query", 80)}`;
    case "recall":
      return op.kind === "docs" ? `docs: ${clip((op.entities || []).join(", "), 70)}` : `recalled “${clip(op.request, 70)}”`;
    case "skill":
      return op.args ? clip(op.args, 80) : "invoked";
    case "artifact":
      return `published ${clip(op.title || op.path || "artifact", 70)}`;
    case "other":
      return `${op.name || "tool"}${
        op.readInto?.length
          ? ` · read by model → ${op.readInto.join(", ")}${op.readIntoMore ? ` +${op.readIntoMore}` : ""}`
          : op.readByModel ? " · read by model" : ""
      }`;
    case "verified":
    case "services":
      return `${op.kind || "op"} · ${clip(op.subject || "", 80)}`;
    case "code":
      return op.kind === "poll"
        ? `polled cell ${op.cellId}${op.status ? ` · ${op.status}` : ""}`
        : op.kind === "stdin"
          ? "sent input to a running cell"
          : op.command
            ? `cell · ${cellCommandLabel(op)}${op.ok === false ? " · errored" : ""}`
            : `ran a code cell · ${op.lines} line${op.lines === 1 ? "" : "s"}${op.ok === false ? " · errored" : ""}`;
    case "agents":
      return op.kind === "merge"
        ? op.summary
          ? `report returned · ${clip(op.summary, 70)}`
          : `merged result back${op.ok === false ? " · errored" : ""}`
        : op.kind === "ack"
          ? op.taskName
            ? `launch acknowledged · ${clip(op.taskName, 60)}`
            : "launch acknowledged · the report returns later as a task notification"
          : op.kind === "send"
            ? "sent a message into the thread (content sealed)"
            : op.kind === "interrupt"
              ? "interrupted the agent"
              : op.kind === "wait"
                ? "waited for agent activity"
                : op.kind === "list"
                  ? "listed the agent roster"
                  : op.kind === "close"
                    ? "closed an agent"
                    : op.flavor === "fanout"
                ? "spawned workflow fan-out"
                : op.flavor === "session"
                  ? "spawned background session"
                  : "spawned sub-agent";
    default:
      return op.kind || "op";
  }
}

// A lane's rail digest: op-kind counts, plus net ± for file lanes.
export function laneDigest(lane) {
  if (lane.domain === "narration") {
    const thinking = lane.items.filter((it) => it.op?.kind === "thinking").length;
    const said = lane.items.length - thinking;
    return [said ? `${said} note${said === 1 ? "" : "s"}` : null, thinking ? `${thinking} from thinking` : null]
      .filter(Boolean)
      .join(" · ");
  }
  if (lane.domain === "harness") {
    const n = lane.items.length;
    return `${n} message${n === 1 ? "" : "s"}`;
  }
  if (lane.domain === "output") {
    const n = lane.items.reduce((sum, it) => sum + (it.op?.count || 1), 0);
    const failed = lane.items.reduce((sum, it) => sum + (it.op?.failed || 0), 0);
    return [`${n} call${n === 1 ? "" : "s"}, output only`, failed ? `${failed} failed` : null].filter(Boolean).join(" \u00b7 ");
  }
  if (lane.domain === "prompt" || lane.domain === "reply") {
    const notices = lane.items.filter((it) => it.op?.kind === "notice").length;
    const n = lane.items.length - notices;
    const word = lane.domain === "prompt" ? "prompt" : "repl";
    const plural = lane.domain === "prompt" ? (n === 1 ? "" : "s") : n === 1 ? "y" : "ies";
    return [n ? `${n} ${word}${plural}` : null, notices ? `${notices} notice${notices === 1 ? "" : "s"}` : null]
      .filter(Boolean)
      .join(" · ");
  }
  const counts = new Map();
  let plus = 0;
  let minus = 0;
  for (const it of lane.items) {
    // An attachment read counts as a VIEW: nothing was read off an image, and a
    // rail row saying "6 reads" for a file with no readable content in the trace
    // is the specific confusion this distinction removes.
    const k = it.op?.media && it.op?.kind === "read"
      ? "view"
      : it.op?.kind || (it.domain === "other" ? "call" : "op");
    counts.set(k, (counts.get(k) || 0) + 1);
    plus += it.op?.plus || 0;
    minus += it.op?.minus || 0;
  }
  const parts = [...counts.entries()].map(([k, n]) => `${n} ${laneKindLabel(k, n)}`);
  let out = parts.join(" · ");
  if (plus + minus > 0) out += ` · +${plus} −${minus}`;
  const readN = lane.items.filter((it) => it.op?.readByModel).length;
  if (readN) out += ` · ${readN} read by model`;
  const verifiedN = lane.items.filter((it) => it.op?.verified).length;
  if (verifiedN) out += ` · ${verifiedN} from model`;
  return out;
}

// ── Narration ───────────────────────────────────────────────────────────────
// The agent's prose ALONGSIDE its work, which the semantic feed drops by
// construction: a reply beat is at most one per episode and only from an
// assistant turn that called no tools, so "here is what I am about to query and
// why" is never a beat. That sentence is the stated reason a call ran, and
// without it a lane can say a query happened but not that it was a decision.
//
// One note per assistant turn, its text blocks joined. A turn that wrote no
// prose contributes its thinking instead, flagged as such — an unnarrated call
// still had a reason, and the alternative is a silent hole in the lane. Turns
// already claimed as reply beats are skipped, so an episode's conclusion reads
// in one place rather than two.
const NOTE_CAP = 700;
const THINKING_NOTE_CAP = 420;
// What a system message's lane and a tool output lane keep of their words,
// the same as a note's; the dock reads the rest from the turn (beatWords).
const MESSAGE_EXCERPT_CAP = NOTE_CAP;

// Some clients resend their prior turns' reasoning as a literal placeholder -
// Codex replays history with `<REDACTED>` where the summary was - and a
// placeholder is not a reason: shown as the note it buries the last real
// narration behind a token that says nothing. Only a block that is NOTHING
// BUT the placeholder is skipped; genuine thinking that merely contains a
// redacted value inline is still the agent's own words.
const PLACEHOLDER_THINKING = /^(?:<redacted>|\[redacted[^\]]*\]|\[encrypted reasoning\])$/i;

/**
 * One assistant turn's note, whole: its text blocks joined, else its thinking
 * (flagged). `{ blockIndex, thinking, text }`, or null when it said and
 * thought nothing.
 */
export function narrationOf(turn) {
  let textAt = -1;
  let thoughtAt = -1;
  const said = [];
  const thought = [];
  for (const [i, b] of (turn?.blocks || []).entries()) {
    if (b?.type === "text" && typeof b.text === "string" && b.text.trim()) {
      if (textAt < 0) textAt = i;
      said.push(b.text.trim());
    } else if (b?.type === "thinking" && typeof b.thinking === "string" && b.thinking.trim()
      && !PLACEHOLDER_THINKING.test(b.thinking.trim())) {
      if (thoughtAt < 0) thoughtAt = i;
      thought.push(b.thinking.trim());
    }
  }
  const thinking = said.length === 0;
  const parts = thinking ? thought : said;
  if (!parts.length) return null;
  return { blockIndex: thinking ? thoughtAt : textAt, thinking, text: parts.join("\n\n") };
}

export function extractNarration(turns, skipTurns = new Set()) {
  const out = [];
  (Array.isArray(turns) ? turns : []).forEach((turn, turnIndex) => {
    if (turn?.role !== "assistant" || skipTurns.has(turnIndex)) return;
    const note = narrationOf(turn);
    if (!note) return;
    const cap = note.thinking ? THINKING_NOTE_CAP : NOTE_CAP;
    out.push({
      turnIndex,
      blockIndex: note.blockIndex,
      thinking: note.thinking,
      text: note.text.length > cap ? note.text.slice(0, cap) : note.text,
      truncated: note.text.length > cap,
    });
  });
  return out;
}

/** A note's words, whole: the narration lane keeps a capped excerpt, the turn the rest. */
export function noteWords(turns, note) {
  if (!note?.truncated) return note?.text || "";
  return narrationOf(turns?.[note.turnIndex])?.text || note.text || "";
}

// The note a mark was made UNDER: the latest one at or before the mark's own
// position. Prose blocks precede tool_use blocks inside an assistant turn, so a
// call narrated in its own turn pairs with that turn's note; an unnarrated call
// pairs with the last thing said before it, which is the standing reason it ran.
// `narration` must be turn-ascending — extractNarration's output is.
export function noteBefore(narration, turnIndex, blockIndex = 0) {
  if (turnIndex == null) return null;
  let best = null;
  for (const n of narration || []) {
    if (n.turnIndex > turnIndex) break;
    if (n.turnIndex === turnIndex && n.blockIndex > blockIndex) break;
    best = n;
  }
  return best;
}

// The note a mark answers to. A call that carries its own description - the
// Bash tool's `description` input, the same field in Codex's exec wrapper -
// stated its reason ON the call, and that outranks the nearest note:
// `noteBefore` is a proximity heuristic, and on a trace whose thinking comes
// back placeholder-redacted the nearest surviving note can be twenty turns of
// work away from the call it gets pinned to.
export function noteFor(turns, narration, turnIndex, blockIndex = 0) {
  const described = callDescriptionOf(turns?.[turnIndex]?.blocks?.[blockIndex]);
  if (described) {
    return { turnIndex, blockIndex, thinking: false, described: true, text: described, truncated: false };
  }
  return noteBefore(narration, turnIndex, blockIndex);
}

// ── File path tree ──────────────────────────────────────────────────────────
// Same shape discipline as buildFileTree (single-child chains compress so the
// rail reads like a PR file list), but the leaves are LANES, and it spans one
// trace rather than a scan window of endpoints.

function newDir(name, path) {
  return { name, path, dirs: new Map(), lanes: [] };
}

function insertLane(root, lane) {
  const segs = String(lane.path).replace(/\\/g, "/").split("/").filter(Boolean);
  let node = root;
  for (let i = 0; i < segs.length - 1; i++) {
    const seg = segs[i];
    let child = node.dirs.get(seg);
    if (!child) {
      child = newDir(seg, node.path ? `${node.path}/${seg}` : seg);
      node.dirs.set(seg, child);
    }
    node = child;
  }
  node.lanes.push(lane);
}

function compressChains(node) {
  for (const child of node.dirs.values()) compressChains(child);
  const merged = new Map();
  for (let child of node.dirs.values()) {
    while (child.dirs.size === 1 && child.lanes.length === 0) {
      const only = child.dirs.values().next().value;
      only.name = `${child.name}/${only.name}`;
      child = only;
    }
    merged.set(child.name, child);
  }
  node.dirs = merged;
}

function sortDir(node) {
  node.dirs = new Map([...node.dirs.entries()].sort((a, b) => a[0].localeCompare(b[0])));
  node.lanes.sort((a, b) => a.label.localeCompare(b.label));
  for (const child of node.dirs.values()) sortDir(child);
}

function laneCountOf(node) {
  let n = node.lanes.length;
  for (const child of node.dirs.values()) n += laneCountOf(child);
  return n;
}

// The conversation's own lanes, in reading order. The two EPISODE BOUNDARIES
// pair at the top — what was asked, what was concluded — then what the
// harness sent as system, and the running commentary sits last, against the
// work groups below it: a note explains the marks it precedes, so the shorter
// the eye travel to them the better. The vertical axis is relatedness, not
// time; time runs along the columns. Membership doubles as the test for "this
// lane is conversation, not work", which decides both its group and its mark.
const CONVERSATION_RANK = { prompt: 0, reply: 1, harness: 2, narration: 3 };

export const isConversationDomain = (domain) => Object.hasOwn(CONVERSATION_RANK, String(domain));

// A message's words as its system lane reads them: its text parts, verbatim.
function harnessWordsOf(blocks) {
  let first = -1;
  const parts = [];
  (blocks || []).forEach((b, i) => {
    if (b?.type !== "text" || typeof b.text !== "string" || !b.text) return;
    if (first < 0) first = i;
    parts.push(b.text);
  });
  return parts.length ? { text: parts.join("\n\n"), first } : null;
}

/**
 * What a conversation lane's item said, whole. Its op carries a capped excerpt
 * for the lanes and their hovers; the turn holds every word, read the way the
 * item was (a prompt without the harness's scaffolding, a reply's text, a
 * note's prose or else its thinking, a system message's text).
 */
export function beatWords(turns, item) {
  const turn = item?.turnIndex == null ? null : turns?.[item.turnIndex];
  const words = !turn ? null
    : item.domain === "prompt" ? promptWordsOf(turn.blocks)
      : item.domain === "reply" ? replyWordsOf(turn.blocks)
        : item.domain === "narration" ? narrationOf(turn)
          : item.domain === "harness" ? harnessWordsOf(turn.blocks) : null;
  return words?.text ?? item?.op?.text ?? "";
}

// ── The model ───────────────────────────────────────────────────────────────
// turns → {
//   lanes:    [{ id, domain, label, path?, items, cells: Map(turn→[items]),
//                turns: [occupied turn indices], unplaced, digest }]
//   laneById: Map(id → lane)
//   groups:   [{ id, kind: "files"|"domain", label, lanes, tree? }]
//   columns:  [{ turn, gapBefore }]   — occupied turns only, ascending;
//                                       gapBefore = untouched turns before this one
//   beats:    Map(turn → { prompt?, reply? })   — episode boundaries for the ruler
//   turnCount: total turns in the trace — the axis runs 0..turnCount-1, every
//              turn addressable whether or not anything semantic landed on it
//   narration: extractNarration's notes, turn-ascending — the same prose the
//              narration lane marks, kept flat so a work mark can be paired
//              with the note it was made under (noteBefore)
//   coverage: buildSemanticFeed's { toolUses, claimed, residual }
//   actions:  Map(actionId -> { action, packets: [{ itemId, laneId, turn }] })
//              - every work packet under the call it came from (traceActions)
//   actionByItemId: Map(item id -> actionId)
// }
// `actions` is the caller's buildTraceActions table for these turns, when it
// already built one for the shell door.
// The lanes that did something at a turn. A tool result has no cell of its
// own (its call sits at the turn that made it), so a turn with none moves back
// to the nearest earlier turn that has one: { turn, lanes }.
export function lanesAtTurn(model, turn) {
  const at = (t) => (model?.lanes || []).filter((l) => l.cells?.has(t));
  let t = turn;
  while (t > 0 && !at(t).length) t -= 1;
  return { turn: t, lanes: at(t) };
}

export function buildTraceLanes(turns, { extraItems = [], readByModel = null, readInto = null, actions = null } = {}) {
  const feed = buildSemanticFeed(turns);
  const domains = feedDomains();
  const safeTurns = Array.isArray(turns) ? turns : [];

  // A residual op carries only the tool's name — the extractors never claimed
  // it, so nothing recorded whether it FAILED. Pair failures back in from the
  // raw results so an errored unclassified call marks red like any other.
  const failedCalls = new Set();
  const resultBlocks = new Map();
  for (const turn of safeTurns) {
    for (const b of turn?.blocks || []) {
      if (b?.type !== "tool_result" || !b.tool_use_id) continue;
      if (b.isError) failedCalls.add(b.tool_use_id);
      if (!resultBlocks.has(b.tool_use_id)) resultBlocks.set(b.tool_use_id, b);
    }
  }

  const resultTextOf = (b) => {
    const c = b?.content;
    if (typeof c === "string") return c;
    if (Array.isArray(c)) return c.map((p) => (typeof p === "string" ? p : p?.text || "")).join(" ");
    return "";
  };

  // Codex spawn identities. A spawn's task_name comes from its INPUT when the
  // model chose one, else from its RESULT receipt ({"task_name": …}). The
  // task→spawn map is what lets a later send_message/interrupt_agent mark
  // land on the lane it addressed — the message body is sealed (encrypted),
  // the addressing is not.
  const spawnTaskById = new Map();
  const spawnIdByTask = new Map();
  for (const turn of safeTurns) {
    for (const b of turn?.blocks || []) {
      if (b?.type !== "tool_use" || !b.id) continue;
      const name = String(b.name || "");
      if (name !== "spawn_agent" && name !== "followup_task") continue;
      // Both spellings of the child's name address it: the short task_name the
      // model chose in the INPUT ("auth_tenant") and the full registered path
      // the RECEIPT returns ("/root/auth_tenant_20260817_0615"). A later
      // send_message targets the registered path, so indexing only the input
      // name strands every send in the residual.
      const named = name === "followup_task" && !b.input?.task_name ? b.input?.target : b.input?.task_name;
      const fromInput = typeof named === "string" && named.trim() ? named : null;
      const fromReceipt = spawnAckTaskName(resultTextOf(resultBlocks.get(b.id)));
      const tn = fromReceipt || fromInput;
      if (!tn) continue;
      spawnTaskById.set(b.id, tn);
      for (const alias of [fromInput, fromReceipt]) {
        if (alias && !spawnIdByTask.has(alias)) spawnIdByTask.set(alias, b.id);
      }
    }
  }

  // Built up front from every file path in the feed, because a pairing is
  // attested by the paths as a SET: the first op cannot know that a later one
  // will address the same file through the other mount.
  const relations = pathRelations(
    feed.rows.flatMap((row) => (row.items || []).filter((it) => it.domain === "files").map((it) => it.op?.path)),
  );

  const beats = new Map();
  const laneMap = new Map();
  const occupied = new Set();

  const laneFor = (id, init) => {
    let lane = laneMap.get(id);
    if (!lane) {
      lane = { id, items: [], cells: new Map(), turns: [], unplaced: 0, ...init };
      laneMap.set(id, lane);
    }
    return lane;
  };

  for (const row of feed.rows) {
    if (row.type === "prompt" || row.type === "reply") {
      const t = row.item.turnIndex;
      if (t == null) continue;
      const slot = beats.get(t) || {};
      slot[row.type] = row.item;
      beats.set(t, slot);
      occupied.add(t);
      // The conversation holds lanes of its own: what was asked and what was
      // answered are subjects exactly like a file is — the beats stay on the
      // ruler as episode geometry, and these lanes make each one selectable.
      const lane = laneFor(row.type === "prompt" ? "prompt:all" : "reply:all", {
        domain: row.type,
        label: row.type === "prompt" ? "user" : "agent",
      });
      lane.items.push(row.item);
      let cell = lane.cells.get(t);
      if (!cell) {
        cell = [];
        lane.cells.set(t, cell);
        lane.turns.push(t);
      }
      cell.push(row.item);
      continue;
    }
    for (let item of row.items) {
      if (item.domain === "other" && failedCalls.has(item.id.slice("other:".length))) {
        item = { ...item, op: { ...item.op, ok: false } };
      }
      // A residual call the reading judged keeps its residual mark — coverage
      // is the extractors' number — but the mark now SAYS it was read, and
      // NAMES where its work was re-labelled: "Bash · read by model → read
      // backend/README.md" is a pointer; a bare flag repeated per mark is
      // noise.
      if (item.domain === "other") {
        const base = item.id.slice("other:".length).split("#")[0];
        if (readByModel?.has(base)) {
          const into = typeof readInto?.get === "function" ? readInto.get(base) : null;
          item = {
            ...item,
            op: {
              ...item.op,
              readByModel: true,
              ...(into?.length ? { readInto: into.slice(0, 2), readIntoMore: Math.max(0, into.length - 2) } : {}),
            },
          };
        }
      }
      let lane;
      // Sub-agent spawns get a lane PER CALL, not per tool name: each Agent
      // invocation is its own thread of work, and the lane is what lets the
      // reader follow that thread (spawn mark here, merge mark where its
      // result lands, added after this loop). The item keeps its residual
      // block-id key, so the coverage math is untouched -- these calls stay
      // "unclassified" to the extractors; only their lane home changes.
      // Spawn detection is name + SHAPE, centralized in traceAgentSpawns.js
      // (the corpus-evidenced rule set). Anything it rejects falls through to
      // the residual lane like any other unclaimed call.
      const spawnBlock = item.domain === "other"
        ? safeTurns[item.turnIndex]?.blocks?.[item.blockIndex]
        : null;
      const spawn = spawnBlock ? spawnFromToolUse(spawnBlock.name, spawnBlock.input) : null;
      if (spawn) {
        // A sealed Codex spawn still has an identity: the task name, from the
        // input or recovered from the launch receipt. It names the lane.
        const taskName = spawn.taskName || spawnTaskById.get(spawnBlock.id) || "";
        lane = laneFor(`agents:${spawnBlock.id || `${item.turnIndex}:${item.blockIndex}`}`, {
          domain: "agents",
          label: taskName ? clip(taskNameLabel(taskName) || spawn.label, 44) : spawn.label,
          agentType: spawn.agentType,
          spawnPrompt: spawn.spawnPrompt,
          flavor: spawn.flavor,
          taskName,
          ...(spawn.spawnToken ? { spawnToken: spawn.spawnToken } : {}),
        });
        lane.items.push({
          ...item,
          domain: "agents",
          op: { ...item.op, kind: "spawn", flavor: spawn.flavor, ...(taskName ? { taskName } : {}) },
        });
        if (item.turnIndex != null) {
          occupied.add(item.turnIndex);
          lane.cells.set(item.turnIndex, [lane.items[lane.items.length - 1]]);
          lane.turns.push(item.turnIndex);
        }
        continue;
      }
      // Codex's target-less agent collectors — wait_agent, list_agents,
      // close_agent — are agent-lifecycle plumbing (traceAgentSpawns calls
      // them "collectors, not spawners"). They can't land on a specific
      // child's lane, but a residual lane per tool name misfiles them as
      // unknowns: they get one plumbing lane in the Agents group. Same
      // residual-accounting rule as spawns — only the lane home changes.
      // `collector: true` is what the view keys OFF of: no single child
      // thread sits behind this lane, so it must not offer the splice-in
      // timeline toggle the spawn lanes get.
      const collectorKind = spawnBlock
        ? { wait_agent: "wait", list_agents: "list", close_agent: "close" }[String(spawnBlock.name || "")]
        : null;
      if (collectorKind) {
        lane = laneFor("agents:collectors", { domain: "agents", label: "agent waits", collector: true });
        const mark = { ...item, domain: "agents", op: { ...item.op, kind: collectorKind } };
        lane.items.push(mark);
        if (item.turnIndex != null) {
          occupied.add(item.turnIndex);
          let cell = lane.cells.get(item.turnIndex);
          if (!cell) {
            cell = [];
            lane.cells.set(item.turnIndex, cell);
            lane.turns.push(item.turnIndex);
          }
          cell.push(mark);
        }
        continue;
      }
      // send_message / interrupt_agent into a spawned thread: the mark
      // belongs on that thread's lane, keyed by the plaintext target. Same
      // residual-accounting rule as spawns — only the lane home changes.
      const msg = spawnBlock ? agentTargetOf(spawnBlock.name, spawnBlock.input) : null;
      const msgHome = msg && spawnIdByTask.has(msg.target)
        ? laneMap.get(`agents:${spawnIdByTask.get(msg.target)}`)
        : null;
      if (msgHome) {
        const mark = { ...item, domain: "agents", op: { ...item.op, kind: msg.kind, target: msg.target } };
        msgHome.items.push(mark);
        if (item.turnIndex != null) {
          occupied.add(item.turnIndex);
          let cell = msgHome.cells.get(item.turnIndex);
          if (!cell) {
            cell = [];
            msgHome.cells.set(item.turnIndex, cell);
            msgHome.turns.push(item.turnIndex);
          }
          cell.push(mark);
        }
        continue;
      }
      if (item.domain === "files") {
        // Each path keeps its own lane, at the spelling the tool used. When two
        // of them are the same file behind two mounts, they carry the relation
        // rather than being merged into one row (see tracePathAliases).
        const path = item.op?.path || "unknown";
        const segs = String(path).replace(/\\/g, "/").split("/").filter(Boolean);
        lane = laneFor(`files:${path}`, {
          domain: "files",
          label: segs[segs.length - 1] || path,
          path,
          sameFileAs: relations.siblingsOf(path),
          mountGroup: relations.groupOf(path),
        });
      } else {
        const ent = laneEntityOf(item.domain, item.op);
        const domainLabel = domains[item.domain]?.label || item.domain;
        lane = laneFor(`${item.domain}:${ent.key}`, { domain: item.domain, label: ent.label || domainLabel });
      }
      lane.items.push(item);
      if (item.turnIndex == null) {
        lane.unplaced += 1;
        continue;
      }
      occupied.add(item.turnIndex);
      let cell = lane.cells.get(item.turnIndex);
      if (!cell) {
        cell = [];
        lane.cells.set(item.turnIndex, cell);
        lane.turns.push(item.turnIndex);
      }
      cell.push(item);
    }
  }

  // Second half of each sub-agent lane: where the spawned thread's results
  // re-enter this conversation. A synchronous spawn's tool_result IS the
  // report - the merge mark. An ASYNC spawn's tool_result is only a launch
  // acknowledgement; the report arrives later as an in-band
  // <task-notification> naming the spawn's own tool_use id, and a resumed
  // agent notifies more than once, so every arrival gets a merge mark. A
  // lane with no result yet keeps only its spawn mark.
  for (const lane of laneMap.values()) {
    if (lane.domain !== "agents") continue;
    const spawn = lane.items[0];
    // Only spawn-headed lanes have a launch to pair a report with — the
    // collectors lane's first item is a wait, and scanning its result would
    // mint a merge for a timeout message.
    if (spawn?.op?.kind !== "spawn") continue;
    const blockId = spawn?.id?.startsWith("other:") ? spawn.id.slice("other:".length) : null;
    if (!blockId || spawn.turnIndex == null) continue;
    const addMark = (ti, bi, op) => {
      const mark = { ...spawn, turnIndex: ti, blockIndex: bi, op };
      lane.items.push(mark);
      occupied.add(ti);
      let cell = lane.cells.get(ti);
      if (!cell) {
        cell = [];
        lane.cells.set(ti, cell);
        lane.turns.push(ti);
      }
      cell.push(mark);
    };
    let result = null;
    for (let ti = spawn.turnIndex; ti < safeTurns.length && !result; ti++) {
      const blocks = safeTurns[ti]?.blocks || [];
      const bi = blocks.findIndex((b) => b?.type === "tool_result" && b.tool_use_id === blockId);
      if (bi >= 0) result = { ti, bi, block: blocks[bi] };
    }
    let reports = 0;
    for (let ti = spawn.turnIndex; ti < safeTurns.length; ti++) {
      const turn = safeTurns[ti];
      // Notifications are harness-injected: newer harnesses deliver them as
      // MID-CONVERSATION SYSTEM turns, older ones inside user turns. Never
      // assistant turns - a model quoting a frame must not mint a merge.
      if (turn?.role !== "user" && turn?.role !== "system") continue;
      const blocks = turn.blocks || [];
      for (let bi = 0; bi < blocks.length; bi++) {
        if (blocks[bi]?.type !== "text") continue;
        for (const n of taskNotificationsIn(blocks[bi].text)) {
          if (n.toolUseId !== blockId) continue;
          addMark(ti, bi, {
            ...spawn.op,
            kind: "merge",
            summary: n.summary || "",
            report: String(n.result || ""),
          });
          reports += 1;
        }
      }
    }
    if (result && result.ti !== spawn.turnIndex) {
      // A Codex spawn's result is a launch receipt ({"task_name": …}), never
      // the child's report — presenting it as the merge point would close a
      // thread that is still running. A follow-up's is empty, for the same
      // reason: a Codex child reports back in a frame of its own (below).
      const isAck = reports > 0
        || isAsyncLaunchAck(resultTextOf(result.block))
        || Boolean(spawnAckTaskName(resultTextOf(result.block)))
        || Boolean(lane.taskName);
      addMark(result.ti, result.bi, isAck
        ? { ...spawn.op, kind: "ack" }
        : { ...spawn.op, kind: "merge", ...(result.block.isError ? { ok: false } : {}) });
    }
  }

  // A Codex child's report re-enters as a USER turn framed with the child's
  // own task path as its sender - not as the result of any call, so the pass
  // above never sees it. It merges into the lane of the latest spawn or
  // follow-up addressed to that task before it: the report is the content the
  // two threads share, and a merge mark is where the joins pin it.
  const taskLanes = [...laneMap.values()].filter((lane) => lane.domain === "agents" && lane.taskName
    && lane.items[0]?.op?.kind === "spawn" && lane.items[0].turnIndex != null);
  if (taskLanes.length) {
    const sameTask = (a, b) => a === b || (taskNameLabel(a) && taskNameLabel(a) === taskNameLabel(b));
    for (let ti = 0; ti < safeTurns.length; ti++) {
      if (safeTurns[ti]?.role !== "user") continue;
      const blocks = safeTurns[ti].blocks || [];
      for (let bi = 0; bi < blocks.length; bi++) {
        if (blocks[bi]?.type !== "text") continue;
        for (const msg of codexAgentMessagesIn(blocks[bi].text)) {
          const lane = taskLanes
            .filter((l) => l.items[0].turnIndex <= ti && sameTask(l.taskName, msg.sender))
            .sort((a, b) => b.items[0].turnIndex - a.items[0].turnIndex)[0];
          if (!lane) continue;
          const spawn = lane.items[0];
          const report = msg.sealed ? "" : String(msg.payload || "").trim();
          const mark = {
            ...spawn, turnIndex: ti, blockIndex: bi,
            op: { ...spawn.op, kind: "merge", summary: msg.type.toLowerCase().replace(/_/g, " "), report },
          };
          lane.items.push(mark);
          occupied.add(ti);
          let cell = lane.cells.get(ti);
          if (!cell) {
            cell = [];
            lane.cells.set(ti, cell);
            lane.turns.push(ti);
          }
          cell.push(mark);
        }
      }
    }
  }

  // Verified readings — the reading agent's quote-anchored additions — join
  // the same lanes the drafted ops live on: a verified file read lands on its
  // path's lane in the tree, a verified forge call on its PR's lane. They
  // arrive OUTSIDE the feed on purpose: coverage counts what the extractors
  // claimed, and a model-verified reading is a different tier of knowledge,
  // not a claim. What fits no known domain groups under "verified" rather
  // than evaporating.
  for (const item of extraItems || []) {
    if (item?.turnIndex == null || !item.op) continue;
    let lane;
    if (item.domain === "files") {
      const path = item.op.path || "unknown";
      const segs = String(path).replace(/\\/g, "/").split("/").filter(Boolean);
      lane = laneFor(`files:${path}`, {
        domain: "files",
        label: segs[segs.length - 1] || path,
        path,
        sameFileAs: relations.siblingsOf(path),
        mountGroup: relations.groupOf(path),
      });
    } else {
      const ent = laneEntityOf(item.domain, item.op);
      lane = laneFor(`${item.domain}:${ent.key}`, {
        domain: item.domain,
        label: ent.label || domains[item.domain]?.label || item.domain,
      });
    }
    lane.items.push(item);
    occupied.add(item.turnIndex);
    let cell = lane.cells.get(item.turnIndex);
    if (!cell) {
      cell = [];
      lane.cells.set(item.turnIndex, cell);
      lane.turns.push(item.turnIndex);
    }
    cell.push(item);
  }

  // The narration lane, from the turns rather than from the feed: prose is not a
  // tool call, so it claims no block and moves neither the coverage numbers nor
  // the residual. Its turns DO join the occupied set — an assistant turn that
  // only talked is a turn where something happened, and leaving it out of the
  // axis is what made the conversation look like one prompt and nothing else.
  const replyTurns = new Set(
    feed.rows.filter((r) => r.type === "reply").map((r) => r.item?.turnIndex).filter((t) => t != null),
  );
  const narration = extractNarration(safeTurns, replyTurns);
  if (narration.length) {
    const lane = laneFor("narration:all", { domain: "narration", label: "commentary" });
    for (const n of narration) {
      const item = {
        id: `narration:${n.turnIndex}`,
        domain: "narration",
        turnIndex: n.turnIndex,
        blockIndex: n.blockIndex,
        op: { kind: n.thinking ? "thinking" : "note", text: n.text, truncated: n.truncated },
      };
      lane.items.push(item);
      occupied.add(n.turnIndex);
      lane.cells.set(n.turnIndex, [item]);
      lane.turns.push(n.turnIndex);
    }
  }

  // What else the session carried, so that no message goes unmarked: a
  // message the harness sent as system (its tool lists, token counters, task
  // notifications), a conversation lane; and results whose call is not in the
  // record (a capture that kept what went to the model but not the calls the
  // model made, or a call on a page not read), each a call of a tool the
  // record does not name, a work lane beside the calls no extractor claimed.
  // Each lane exists only when the session has them.
  const called = new Set();
  for (const turn of safeTurns) {
    for (const b of turn?.blocks || []) if (b?.type === "tool_use" && b.id && !b.virtual) called.add(b.id);
  }
  const place = (lane, item) => {
    lane.items.push(item);
    occupied.add(item.turnIndex);
    lane.cells.set(item.turnIndex, [item]);
    lane.turns.push(item.turnIndex);
  };
  safeTurns.forEach((turn, turnIndex) => {
    const blocks = turn?.blocks || [];
    if (turn?.role === "system") {
      const words = harnessWordsOf(blocks);
      const text = words?.text || "";
      place(laneFor("harness:all", { domain: "harness", label: "system" }), {
        id: `harness:${turnIndex}`,
        domain: "harness",
        turnIndex,
        blockIndex: words ? words.first : 0,
        op: { kind: "system", text: text.slice(0, MESSAGE_EXCERPT_CAP), truncated: text.length > MESSAGE_EXCERPT_CAP },
      });
    }
    const unpaired = [];
    blocks.forEach((b, i) => {
      if (b?.type === "tool_result" && !b.virtual && !(b.tool_use_id && called.has(b.tool_use_id))) unpaired.push(i);
    });
    if (unpaired.length) {
      const text = toolResultText(blocks[unpaired[0]].content);
      const failed = unpaired.filter((i) => blocks[i].isError ?? blocks[i].is_error).length;
      place(laneFor("output:all", { domain: "output", label: "unknown tool" }), {
        id: `output:${turnIndex}`,
        domain: "output",
        turnIndex,
        blockIndex: unpaired[0],
        op: {
          kind: "output",
          count: unpaired.length,
          blocks: unpaired,
          text: text.slice(0, MESSAGE_EXCERPT_CAP),
          truncated: text.length > MESSAGE_EXCERPT_CAP,
          ...(failed ? { ok: false, failed } : {}),
        },
      });
    }
  });

  const lanes = [...laneMap.values()];
  for (const lane of lanes) {
    lane.turns.sort((a, b) => a - b);
    lane.digest = laneDigest(lane);
  }

  // Conversation first — requests and responses are the session's spine, so
  // they read above the work they caused. Then the files tree, then every
  // other domain as a flat group ordered by first touch, in the feed's own
  // domain order with residual ("other") pinned last.
  const groups = [];
  const convLanes = lanes.filter((l) => isConversationDomain(l.domain));
  if (convLanes.length) {
    const ordered = [...convLanes].sort((a, b) => CONVERSATION_RANK[a.domain] - CONVERSATION_RANK[b.domain]);
    groups.push({ id: "domain:conversation", kind: "domain", domain: "conversation", label: "Conversation", lanes: ordered, laneCount: ordered.length });
  }
  // Sub-agent threads directly under the conversation: they ARE conversation
  // (each lane is a prompt the agent sent and the reply it merged back),
  // ordered by spawn turn so parallel fan-outs read left to right.
  const agentLanes = lanes
    .filter((l) => l.domain === "agents")
    .sort((a, b) => (a.turns[0] ?? Infinity) - (b.turns[0] ?? Infinity));
  if (agentLanes.length) {
    groups.push({ id: "domain:agents", kind: "domain", domain: "agents", label: "Agents", lanes: agentLanes, laneCount: agentLanes.length });
  }
  const fileLanes = lanes.filter((l) => l.domain === "files");
  if (fileLanes.length) {
    const root = newDir("", "");
    for (const lane of fileLanes) insertLane(root, lane);
    compressChains(root);
    sortDir(root);
    groups.push({ id: "files", kind: "files", label: domains.files?.label || "Files", lanes: fileLanes, tree: root, laneCount: laneCountOf(root) });
  }
  const rest = lanes.filter(
    (l) => l.domain !== "files" && l.domain !== "agents" && !isConversationDomain(l.domain),
  );
  const domainOrder = Object.keys(domains).filter((d) => d !== "prompt" && d !== "reply" && d !== "files" && d !== "other");
  // Output whose call the record lacks is a call nothing could classify: it
  // reads with the unclassified calls.
  const groupDomain = (d) => (d === "output" ? "other" : d);
  const seenDomains = [...new Set(rest.map((l) => groupDomain(l.domain)))];
  const ordered = [
    ...domainOrder.filter((d) => seenDomains.includes(d)),
    ...seenDomains.filter((d) => d !== "other" && !domainOrder.includes(d)),
    ...(seenDomains.includes("other") ? ["other"] : []),
  ];
  // Groups outside the feed's vocabulary say WHAT their lanes are, not how
  // the reading knows about them — "verified" is an epistemic tier (it lives
  // on the marks and op lines), never a subject. And once a reading has
  // judged the extractor-unclaimed calls, "Unclassified" is no longer the
  // whole truth: no extractor claimed them, but the model read them.
  const EXTRA_GROUP_LABELS = { verified: "Model readings", services: "Services" };
  for (const domain of ordered) {
    const ls = rest
      .filter((l) => groupDomain(l.domain) === domain)
      .sort((a, b) => (a.turns[0] ?? Infinity) - (b.turns[0] ?? Infinity));
    const label = domain === "other" && ls.some((l) => l.items?.some((it) => it.op?.readByModel))
      ? "Unclassified · read by model"
      : EXTRA_GROUP_LABELS[domain] || domains[domain]?.label || domain;
    groups.push({ id: `domain:${domain}`, kind: "domain", domain, label, lanes: ls, laneCount: ls.length });
  }

  const turnsAsc = [...occupied].sort((a, b) => a - b);
  const columns = turnsAsc.map((turn, i) => ({
    turn,
    gapBefore: i === 0 ? turn : turn - turnsAsc[i - 1] - 1,
  }));

  // Every packet under the call that produced it, whichever door read it: one
  // call seen on several lanes is how a cell can say what else that call
  // touched. The conversation's own lanes carry no call.
  const table = actions || buildTraceActions(safeTurns);
  const actionIndex = new Map();
  const actionByItemId = new Map();
  for (const lane of lanes) {
    if (isConversationDomain(lane.domain)) continue;
    for (const it of lane.items) {
      const id = actionIdOf(it, table.byId);
      if (!id) continue;
      actionByItemId.set(it.id, id);
      let entry = actionIndex.get(id);
      if (!entry) {
        entry = { action: table.byId.get(id), packets: [] };
        actionIndex.set(id, entry);
      }
      entry.packets.push({ itemId: it.id, laneId: lane.id, turn: it.turnIndex, item: it });
    }
  }

  return {
    lanes,
    laneById: laneMap,
    groups,
    columns,
    beats,
    turnCount: Array.isArray(turns) ? turns.length : 0,
    narration,
    coverage: feed.coverage,
    actions: actionIndex,
    actionByItemId,
  };
}

// A call's home lanes - the residual row of a call no extractor claimed, the
// code cells row - say where the call was issued, not what it touched, so
// they never answer "what else did this call touch".
const HOME_DOMAINS = new Set(["other", "code"]);

// What a call did to one subject, in a word. Ops whose kind has no verb here
// fall back to the mark vocabulary: a write is "wrote", anything else "read".
const CALL_VERBS = {
  read: "read", write: "wrote", create: "wrote", edit: "edited", run: "ran",
  push: "pushed", publish: "published", browse: "browsed", fetch: "fetched",
  search: "searched", query: "queried", spawn: "spawned", merge: "merged", send: "sent",
};

function callVerb(items) {
  if (items.some((it) => it.op?.ok === false)) return "failed";
  const authoring = items.find((it) => markKindOf(it) === "write");
  const it = authoring || items[0];
  return CALL_VERBS[it?.op?.kind] || (authoring ? "wrote" : "read");
}

/**
 * The other subjects the calls behind one cell touched: the files a batched
 * read read beside this one, the subjects of a code cell's other inner calls.
 * Each lane once, at the turn its packet sits on, with what the call did there.
 * `model` says the call reached that subject only through a model's verified
 * reading - a different tier of knowledge, which the reader is told.
 */
export function sameCallLanes(model, laneId, turn) {
  const here = model?.laneById?.get(laneId)?.cells?.get(turn) || [];
  const byLane = new Map();
  for (const it of here) {
    const id = model.actionByItemId?.get(it.id);
    for (const p of (id && model.actions?.get(id)?.packets) || []) {
      if (p.laneId === laneId) continue;
      const lane = model.laneById.get(p.laneId);
      if (!lane || HOME_DOMAINS.has(lane.domain)) continue;
      const at = byLane.get(lane.id);
      if (at) at.items.push(p.item);
      else byLane.set(lane.id, { lane, turn: p.turn, items: [p.item] });
    }
  }
  return [...byLane.values()].map(({ lane, turn: t, items }) => ({
    laneId: lane.id,
    label: lane.path || lane.label || lane.id,
    turn: t,
    verb: callVerb(items),
    model: items.every((it) => it.op?.verified),
  }));
}

/**
 * The cells to ring for a focus - a hovered or selected mark, a folded
 * group's head mark - as [{ lane, turn }]: everything the calls behind it
 * touched on other subjects. A turn focus names no mark, so it rings nothing.
 */
export function callCellsAt(model, focus) {
  const laneIds = focus?.laneIds || (focus?.laneId != null ? [focus.laneId] : []);
  if (!laneIds.length || focus.fromTurn == null) return [];
  const focused = new Set(laneIds);
  const out = new Map();
  for (const laneId of laneIds) {
    for (const t of model?.laneById?.get(laneId)?.turns || []) {
      if (t < focus.fromTurn || t > (focus.toTurn ?? focus.fromTurn)) continue;
      for (const s of sameCallLanes(model, laneId, t)) {
        if (!focused.has(s.laneId)) out.set(`${s.laneId}@${s.turn}`, { lane: s.laneId, turn: s.turn });
      }
    }
  }
  return [...out.values()];
}

// Where a lane's activity sits relative to a turn window — the model behind
// the off-screen pointers. `left` is the nearest occupied turn before the
// window, `right` the nearest after, `visible` whether any mark falls inside.
export function nearestMarks(lane, fromTurn, toTurn) {
  let left = null;
  let right = null;
  let visible = false;
  for (const t of lane?.turns || []) {
    if (t < fromTurn) left = t;
    else if (t > toTurn) {
      if (right == null) right = t;
    } else visible = true;
  }
  return { left, right, visible };
}

// A folded group/dir keeps its timeline: its lanes' cells merge onto the
// header row, so folding never hides WHEN work happened, only which subject
// it landed on. Shared by the lanes view's folded rows and the anatomy hero's
// zoomed-out lanes reading — one merge rule, not two.
export function mergedCells(lanes) {
  const byTurn = new Map();
  for (const lane of lanes || []) {
    for (const [turn, items] of lane.cells) {
      const cur = byTurn.get(turn) || [];
      cur.push(...items);
      byTurn.set(turn, cur);
    }
  }
  return byTurn;
}

// Whether — and which — minor groups fold into one shared row in a zoomed-out
// reading. Real traces put nearly everything in one or two groups and leave a
// tail of groups touched once or twice; a thumbnail cannot scroll past them,
// so groups below max(3, 2% of ops) fuse — but only when there are more than
// three rows to thin AND at least two qualify (folding a single group renames
// it without saving a row). `rows` need `id` and `total`; `combine(minor)`
// builds the shared row from the minority. Pure, so the rule is testable
// without synthesizing multi-domain turn fixtures.
export function foldMinorGroups(rows, combine) {
  if (!Array.isArray(rows) || rows.length <= 3) return rows;
  const totalAll = rows.reduce((a, r) => a + r.total, 0);
  const cut = Math.max(3, Math.ceil(totalAll * 0.02));
  const minor = rows.filter((r) => r.total < cut);
  if (minor.length < 2) return rows;
  const minorIds = new Set(minor.map((r) => r.id));
  return [...rows.filter((r) => !minorIds.has(r.id)), combine(minor)];
}

// Occupied cells merged into fixed-span turn buckets, for zoomed-out rendering:
// when a turn column is narrower than a mark, adjacent activity merges into one
// mark that says how much landed there. `cells` is a lane's Map(turn → items)
// (or any [turn, items] iterable — a folded group's merged cells work the same);
// `span` is how many turns one bucket covers.
export function bucketMarks(cells, span) {
  const s = Math.max(1, Math.floor(span));
  const buckets = new Map();
  for (const [turn, items] of cells?.entries?.() || cells || []) {
    const idx = Math.floor(turn / s);
    let b = buckets.get(idx);
    if (!b) {
      b = { start: idx * s, first: turn, last: turn, items: [] };
      buckets.set(idx, b);
    }
    if (turn < b.first) b.first = turn;
    if (turn > b.last) b.last = turn;
    b.items.push(...items);
  }
  return [...buckets.values()].sort((a, b) => a.start - b.start);
}

// The file as the agent knew it AT a turn: the lane's ops replayed up to and
// including `turn`. Ops the feed could not place (turnIndex null — only the
// command door) are treated as always-included so they can't silently drop
// out of every prefix.
//
// Each DISTINCT turn is a full replay from op 0 — reconstructFile keeps no
// resumable state, and a lane's op count is bounded by how often one path was
// touched in one trace (tens, not thousands), so the prefix replay stays
// cheap. What is NOT acceptable is paying it again for a turn already read:
// scrubbing bounces between adjacent turns and the hover card asks for the
// same (lane, turn) the dock is showing, so results are cached per lane. The
// WeakMap keys on the lane object itself, which is rebuilt whenever the trace
// changes — invalidation is the model rebuild, nothing to clear by hand.
const recCache = new WeakMap();
const REC_CACHE_TURNS = 64;

export function reconstructLaneAsOf(lane, turn) {
  if (!lane) return reconstructFile([]);
  let byTurn = recCache.get(lane);
  if (!byTurn) {
    byTurn = new Map();
    recCache.set(lane, byTurn);
  }
  const hit = byTurn.get(turn);
  if (hit) return hit;
  const ops = (lane.items || [])
    .filter((it) => it.turnIndex == null || it.turnIndex <= turn)
    .map((it) => ({ ...it.op, promptId: it.turnIndex }));
  const rec = reconstructFile(ops);
  if (byTurn.size >= REC_CACHE_TURNS) byTurn.clear();
  byTurn.set(turn, rec);
  return rec;
}

// A cell's dominant mark: error wins (something here failed), then write
// (something here changed the subject), then read. A conversation lane's
// cells are homogeneous, so a beat kind answers immediately.
export function cellKind(items) {
  let kind = "read";
  for (const it of items || []) {
    const k = markKindOf(it);
    if (k === "error") return "error";
    if (k === "prompt" || k === "reply" || k === "note") return k;
    if (k === "write") kind = "write";
    // A cell holding only model readings marks as such; drafted work in the
    // same cell outranks it.
    if ((k === "verified" || k === "readCall") && kind === "read") kind = k;
  }
  return kind;
}
